import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { workerService } from '../services/workerService';

interface Bonus {
  id: number;
  week_start_date: string;
  amount: number;
  description?: string;
}

interface ManageBonusesDialogProps {
  open: boolean;
  onClose: () => void;
  workerId: number;
  onUpdate: () => void;
}

export const ManageBonusesDialog: React.FC<ManageBonusesDialogProps> = ({
  open,
  onClose,
  workerId,
  onUpdate,
}) => {
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    if (open && workerId) {
      loadBonuses();
    }
  }, [open, workerId]);

  const loadBonuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workerService.getWeeklyBonuses(workerId);
      setBonuses(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cargar bonos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bonus: Bonus) => {
    setEditingId(bonus.id);
    setEditAmount(String(bonus.amount));
    setEditDescription(bonus.description || '');
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    try {
      setError(null);
      await workerService.updateBonus(workerId, editingId, {
        amount: parseFloat(editAmount),
        description: editDescription || undefined,
      });
      setEditingId(null);
      await loadBonuses();
      onUpdate();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al actualizar bono');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditAmount('');
    setEditDescription('');
  };

  const handleDelete = async (bonusId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este bono?')) return;
    try {
      setError(null);
      await workerService.deleteBonus(workerId, bonusId);
      await loadBonuses();
      onUpdate();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al eliminar bono');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gestionar bonos</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Typography>Cargando bonos...</Typography>
        ) : bonuses.length === 0 ? (
          <Typography color="textSecondary">No hay bonos registrados.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Semana</strong></TableCell>
                <TableCell><strong>Monto</strong></TableCell>
                <TableCell><strong>Descripción</strong></TableCell>
                <TableCell align="right"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bonuses.map((bonus) => (
                <TableRow key={bonus.id}>
                  <TableCell>{formatDate(bonus.week_start_date)}</TableCell>
                  {editingId === bonus.id ? (
                    <>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="Descripción"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>Cancelar</IconButton>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{formatCurrency(bonus.amount)}</TableCell>
                      <TableCell>{bonus.description || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(bonus)} title="Editar">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(bonus.id)} title="Eliminar">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
